import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

export default function AGB() {
  const { t } = useTranslation();
  const isEN = i18n.language === 'en';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 mb-6 inline-block">
        ← {t('footer.backToHome')}
      </Link>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('footer.agb')}</h1>
      <p className="text-sm text-gray-400 mb-8">{isEN ? 'Version: April 2026' : 'Stand: April 2026'}</p>

      <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">

        {isEN ? (
          <>
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 1 Scope</h2>
              <p>
                These Terms and Conditions apply to the use of the platform <strong>Grenzland Padel Liga</strong>
                {' '}(available at liga.padelmap.app), operated by Timm Buse, Munkbrarup (hereinafter "Operator").
                By registering on the platform, the user accepts these Terms and Conditions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 2 Description of Services</h2>
              <p>
                Grenzland Padel Liga provides a web-based platform for managing padel leagues.
                The platform enables registration for leagues, submission of match results, communication between players,
                and the management of pairs and teams. Use of the platform is free for players; individual leagues may charge
                an entry fee set by the Operator.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 3 Registration and User Account</h2>
              <ol className="list-decimal list-inside space-y-2">
                <li>Use of the platform requires registration with a valid email address.</li>
                <li>The user is obliged to provide truthful information and to keep their access credentials confidential.</li>
                <li>Each natural person may only create one user account.</li>
                <li>The Operator reserves the right to suspend or delete accounts in the event of violations of these Terms or inactivity.</li>
                <li>The user may request deletion of their account at any time by contacting the Operator by email.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 4 League Registration</h2>
              <ol className="list-decimal list-inside space-y-2">
                <li>Registration for a league takes place via the platform within the registration period.</li>
                <li>If an entry fee applies, it will be confirmed manually by the Operator. Payment is arranged in consultation with the Operator.</li>
                <li>
                  Registration is binding. Withdrawal after the registration deadline is only possible in justified exceptional cases
                  (e.g. injury) and requires approval from the Operator.
                </li>
                <li>In the event of withdrawal without valid reason, there is no entitlement to a refund of the entry fee.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 5 Match Operations and Fair Play</h2>
              <ol className="list-decimal list-inside space-y-2">
                <li>All participants are committed to fair and sporting conduct.</li>
                <li>
                  Match appointments are arranged independently between the parties involved. The platform provides a chat function for this purpose.
                  Matches must be completed within the given time frame of the league.
                </li>
                <li>Results must be confirmed by both parties. In the event of discrepancies, the Operator decides.</li>
                <li>Manipulation, no-shows, or refusal to confirm results may result in exclusion from the league.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 6 User Obligations and Prohibited Content</h2>
              <p>The user undertakes not to misuse the platform. In particular, the following are prohibited:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Posting offensive, discriminatory, or unlawful content (e.g. in the chat).</li>
                <li>Sharing access credentials with third parties.</li>
                <li>Automated data extraction (scraping) or technical attacks on the platform.</li>
                <li>Use of false identities or multiple accounts.</li>
              </ul>
              <p className="mt-2">
                In the event of violations, the Operator is entitled to block the user account without prior notice.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 7 Data Protection</h2>
              <p>
                Personal data is processed in accordance with the General Data Protection Regulation (GDPR).
                Further information on data protection can be found in our Privacy Policy.
                In principle, only the data necessary for operating the league is collected (name, email, optional phone number and profile details).
                Data is not sold to third parties or used for advertising purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 8 Limitation of Liability</h2>
              <p>
                The Operator provides the platform without guarantee of uninterrupted availability. The Operator is not liable for:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Technical failures or data loss.</li>
                <li>Damages arising from participation in match operations (physical injuries, property damage).</li>
                <li>Actions or omissions of other users.</li>
              </ul>
              <p className="mt-2">
                Each participant is responsible for their own health and equipment and participates in match operations at their own risk.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 9 Amendments to the Terms</h2>
              <p>
                The Operator reserves the right to amend these Terms at any time with effect for the future.
                Changes will be communicated to users by email or by a notice on the platform.
                If the user does not object within 14 days of the announcement of the changes, the new Terms shall be deemed accepted.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 10 Final Provisions</h2>
              <p>
                The law of the Federal Republic of Germany applies. Place of jurisdiction is Flensburg, to the extent permitted by law.
                Should individual provisions of these Terms be invalid, the validity of the remaining provisions shall not be affected.
              </p>
            </section>
          </>
        ) : (
          <>
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 1 Geltungsbereich</h2>
              <p>
                Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für die Nutzung der Plattform <strong>Grenzland Padel Liga</strong>
                {' '}(erreichbar unter liga.padelmap.app), betrieben von Timm Buse, Munkbrarup (nachfolgend „Betreiber").
                Mit der Registrierung auf der Plattform erkennt der Nutzer diese AGB an.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 2 Leistungsbeschreibung</h2>
              <p>
                Die Grenzland Padel Liga stellt eine webbasierte Plattform zur Verwaltung von Padel-Ligen bereit.
                Die Plattform ermöglicht die Anmeldung zu Ligen, die Eintragung von Spielergebnissen, die Kommunikation zwischen Spielern sowie die Verwaltung
                von Paaren und Mannschaften. Die Nutzung der Plattform ist für Spieler kostenlos; für einzelne Ligen kann eine Startgebühr erhoben werden,
                die vom Betreiber festgelegt wird.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 3 Registrierung und Nutzerkonto</h2>
              <ol className="list-decimal list-inside space-y-2">
                <li>Die Nutzung der Plattform setzt eine Registrierung mit einer gültigen E-Mail-Adresse voraus.</li>
                <li>Der Nutzer ist verpflichtet, wahrheitsgemäße Angaben zu machen und seine Zugangsdaten vertraulich zu behandeln.</li>
                <li>Jede natürliche Person darf nur ein Nutzerkonto anlegen.</li>
                <li>Der Betreiber behält sich vor, Konten bei Verstößen gegen diese AGB oder bei Inaktivität zu sperren oder zu löschen.</li>
                <li>Der Nutzer kann sein Konto jederzeit löschen lassen, indem er den Betreiber per E-Mail kontaktiert.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 4 Anmeldung zu Ligen</h2>
              <ol className="list-decimal list-inside space-y-2">
                <li>Die Anmeldung zu einer Liga erfolgt über die Plattform innerhalb des Anmeldezeitraums.</li>
                <li>Sofern eine Startgebühr anfällt, wird diese vom Betreiber manuell bestätigt. Die Zahlung erfolgt nach Rücksprache mit dem Betreiber.</li>
                <li>
                  Die Anmeldung ist verbindlich. Eine Abmeldung nach Ablauf der Anmeldefrist ist nur in begründeten Ausnahmefällen
                  (z. B. Verletzung) möglich und bedarf der Genehmigung des Betreibers.
                </li>
                <li>Bei Abmeldung ohne triftigen Grund besteht kein Anspruch auf Rückerstattung der Startgebühr.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 5 Spielbetrieb und Fair Play</h2>
              <ol className="list-decimal list-inside space-y-2">
                <li>Alle Teilnehmer verpflichten sich zu fairem und sportlichem Verhalten.</li>
                <li>
                  Spieltermine werden zwischen den beteiligten Parteien eigenständig vereinbart. Die Plattform stellt hierfür eine Chat-Funktion bereit.
                  Spiele sind innerhalb des vorgegebenen Zeitrahmens der Liga zu absolvieren.
                </li>
                <li>Ergebnisse sind von beiden Parteien zu bestätigen. Bei Unstimmigkeiten entscheidet der Betreiber.</li>
                <li>Manipulationen, No-Shows oder Verweigerung der Ergebnisbestätigung können zum Ausschluss aus der Liga führen.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 6 Nutzerpflichten und verbotene Inhalte</h2>
              <p>Der Nutzer verpflichtet sich, die Plattform nicht zu missbrauchen. Insbesondere ist verboten:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Das Einstellen beleidigender, diskriminierender oder rechtswidriger Inhalte (z. B. im Chat).</li>
                <li>Die Weitergabe von Zugangsdaten an Dritte.</li>
                <li>Automatisiertes Auslesen von Daten (Scraping) oder technische Angriffe auf die Plattform.</li>
                <li>Die Nutzung falscher Identitäten oder Mehrfachaccounts.</li>
              </ul>
              <p className="mt-2">
                Bei Verstößen ist der Betreiber berechtigt, das Nutzerkonto ohne Vorwarnung zu sperren.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 7 Datenschutz</h2>
              <p>
                Die Verarbeitung personenbezogener Daten erfolgt gemäß der Datenschutz-Grundverordnung (DSGVO).
                Weitere Informationen zum Datenschutz finden Sie in unserer Datenschutzerklärung.
                Grundsätzlich werden nur die für den Betrieb der Liga notwendigen Daten erhoben (Name, E-Mail, optionale Telefonnummer und Profilangaben).
                Daten werden nicht an Dritte verkauft oder für Werbezwecke genutzt.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 8 Haftungsbeschränkung</h2>
              <p>
                Der Betreiber stellt die Plattform ohne Gewähr für ununterbrochene Verfügbarkeit bereit. Der Betreiber haftet nicht für:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Technische Ausfälle oder Datenverlust.</li>
                <li>Schäden, die durch die Teilnahme am Spielbetrieb entstehen (körperliche Verletzungen, Sachschäden).</li>
                <li>Handlungen oder Unterlassungen anderer Nutzer.</li>
              </ul>
              <p className="mt-2">
                Jeder Teilnehmer trägt die Verantwortung für seine Gesundheit und Ausrüstung und nimmt auf eigenes Risiko am Spielbetrieb teil.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 9 Änderungen der AGB</h2>
              <p>
                Der Betreiber behält sich vor, diese AGB jederzeit mit Wirkung für die Zukunft zu ändern.
                Änderungen werden den Nutzern per E-Mail oder durch einen Hinweis auf der Plattform mitgeteilt.
                Widerspricht der Nutzer nicht innerhalb von 14 Tagen nach Bekanntgabe der Änderungen, gelten die neuen AGB als anerkannt.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">§ 10 Schlussbestimmungen</h2>
              <p>
                Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Flensburg, soweit gesetzlich zulässig.
                Sollten einzelne Bestimmungen dieser AGB unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
              </p>
            </section>
          </>
        )}

      </div>
    </div>
  );
}
